// ======================================================================
// 📁 File: src/modules/ppe/pages/material/boughtout/BoughtOutListPage.jsx
// ======================================================================

import React, { useEffect, useState, useCallback, useContext } from "react";
import axiosClient from "../../../../../api/axiosClient";
import { AuthContext } from "../../../../../context/AuthContext";

import BoughtOutKpiBar from "../../../components/material/boughtout/BoughtOutKpiBar";
import BoughtOutDrawer from "./BoughtOutDrawer";

export default function BoughtOutListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  // ------------------------------------------------------------
  // Load Bought-Out Items
  // ------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/api/ppe/masters/boughtout-items", {
        params: { q: search || undefined },
      });
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load bought-out items", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Bought-Out Item Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            External / Purchased items configuration
          </div>
        </div>

        {permissions?.BOUGHT_OUT_ITEM_CREATE?.create && (
          <button className="btn btn-light" onClick={openCreateDrawer}>
            + Create Bought-Out Item
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <BoughtOutKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by Code or Name"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          className="btn btn-primary"
          onClick={load}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>UOM</th>
                <th>Supplier Ref</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No bought-out items found
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.code}</td>
                  <td>{r.name}</td>
                  <td>{r.uom || "-"}</td>
                  <td>{r.supplier_reference || "-"}</td>
                  <td>
                    {permissions?.BOUGHT_OUT_ITEM_EDIT?.edit && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditDrawer(r)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* REAL DRAWER */}
      {/* ====================================================== */}
      <BoughtOutDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        onClose={closeDrawer}
        onSaved={load}
      />
    </div>
  );
}
